import { useEffect, useMemo, useState } from 'react';

import { focusManager, useMutation, useQuery } from '@tanstack/react-query';

import { doGet, doPost } from 'shared/helpers/http';
import Sentry from 'shared/helpers/sentry';
import { trackEvent } from 'shared/helpers/tracking';

interface TalentEducationExperience {
  major: string;
  degree_type: string;
  institution_name: string;
  graduation_year: string | null;
  graduation_month: string | null;
}

export interface BasicResults {
  full_name: string;
  email: string | null;
  phone: {
    text: string | null;
  };
  current_city: string | null;
  current_state: string | null;
  current_zip_code: string | null;
  years_experience: number;
  certifications: string[];
  talent_education_experiences: TalentEducationExperience[];
  state_licenses: string[];
  total_rn_experience: string;
  worked_in_hospital_in_the_last_year: boolean;
}

export interface WorkExperienceEntry {
  job_title: string;
  company_name: string;
  start_date: string;
  end_date: string | null;
  location: string | null;
  duties: string;
}

export interface WorkExperienceResults {
  summary: string;
  work_experiences: WorkExperienceEntry[];
}

export interface SpecialtyEntry {
  specialty: string;
  years_of_experience: number;
  confidence: 'low' | 'medium' | 'high';
  rationale: string;
  jobs: Array<number | null>;
}

export interface SpecialtyResults {
  specialties: SpecialtyEntry[];
}

export interface AccountInfo {
  email_in_use: boolean;
  phone_in_use: boolean;
  phone_is_valid: boolean;
}

interface ResumeParseResponse {
  status: string;
  basic_results: BasicResults | null;
  work_experience_results: WorkExperienceResults | null;
  specialty_results: SpecialtyResults | null;
  account_info: AccountInfo | null;
}

let parsingStartTime: number | null = null;

const uploadResume = async (file: File) => {
  const formData = new FormData();
  formData.append('resume', file);

  try {
    parsingStartTime = Date.now();
    const response = await doPost('/resume_parse/upload', formData);
    trackEvent('Resume parsing started');

    return response;
  } catch (error) {
    const body = await error?.response?.json()?.catch(() => null);
    throw new Error(body?.error || 'Failed to upload resume');
  }
};

const getResumeParseStatus = async (
  taskId: string
): Promise<ResumeParseResponse> => {
  try {
    const response = await doGet(`/resume_parse/${taskId}`);
    if (['completed', 'failed'].includes(response.status)) {
      const elapsedTime = parsingStartTime
        ? ((Date.now() - parsingStartTime) / 1000).toFixed(1)
        : 0;
      if (response.status === 'completed') {
        trackEvent('Resume parsing completed', { parsingTime: elapsedTime });
      }
      if (response.status === 'failed') {
        trackEvent('Resume parsing failed', { parsingTime: elapsedTime });
      }
      parsingStartTime = null; // Reset the start time after completion
    }

    return response;
  } catch (error) {
    const body = await error?.response?.json()?.catch(() => null);
    throw new Error(body?.error || 'Failed to get resume parse status');
  }
};

const useUploadResume = () => useMutation({ mutationFn: uploadResume });

const useResumeParseStatus = (taskId: string | null) => {
  // Ensure the query remains enabled if the user stops focusing on the window.
  useEffect(() => {
    focusManager.setFocused(true);
    return () => focusManager.setFocused(undefined);
  }, []);

  const [finishedStatus, setFinishedStatus] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    setIsTimeout(false);
    // We don't need a timeout if there isn't an active task id to poll or if
    // the status has already finished.
    if (!taskId || finishedStatus) return () => {};

    const fetchingStartTime = Date.now();
    const timeout = setTimeout(() => {
      setIsTimeout(true);
      trackEvent('Resume parsing status exceeded timeout', {
        parsingTime: ((Date.now() - fetchingStartTime) / 1000).toFixed(1),
      });
      Sentry.captureMessage('Resume parse status exceeded timeout', {
        extra: { taskId },
      });
    }, 15_000);

    return () => clearTimeout(timeout);
  }, [taskId, finishedStatus]);

  const isEnabled = useMemo(
    () => !!taskId && !finishedStatus && !isTimeout,
    [taskId, isTimeout, finishedStatus]
  );

  const query = useQuery({
    queryKey: ['resumeParseStatus', taskId],
    queryFn: () => getResumeParseStatus(taskId),
    enabled: isEnabled,
    refetchInterval: (data) => {
      // Once we have the basic and work experience results, slow down the poll
      if (data?.basic_results && data?.work_experience_results) return 5_000;
      return 500;
    },
    refetchOnWindowFocus: false,
  });

  // The result of the query determines if the query is enabled, creating a
  // circular dependency which we resolve via a state hook, which triggers a
  // re-render when the status concludes.
  useEffect(() => {
    setFinishedStatus(
      ['completed', 'failed'].includes(query.data?.status) || query.isError
    );
  }, [query.data?.status, query.isError]);

  return {
    ...query,
    isTimeout,
    isEnabled,
  };
};

export const useResumeParse = () => {
  const {
    mutate: uploadResumeMutation,
    data: uploadData,
    error: uploadError,
    isLoading: isLoadingUpload,
  } = useUploadResume();

  const taskId = useMemo(() => uploadData?.task_id ?? null, [uploadData]);

  const {
    data,
    error: parseError,
    isLoading: isLoadingParse,
    isTimeout,
  } = useResumeParseStatus(taskId);

  const isError = useMemo(
    () => !!(uploadError || parseError) || isTimeout,
    [uploadError, parseError, isTimeout]
  );

  const isComplete = useMemo(() => {
    // If either call is loading, we aren't complete
    if (isLoadingUpload || (!uploadError && isLoadingParse)) return false;

    // If there is an error, or the status returned and end state
    return isError || ['completed', 'failed'].includes(data?.status);
  }, [isError, uploadError, isLoadingUpload, isLoadingParse, data?.status]);

  const resumeBasicResults = data?.basic_results;
  const resumeWorkExperienceResults = data?.work_experience_results;
  const resumeSpecialtyResults = data?.specialty_results;
  const accountInfo = data?.account_info;

  return {
    uploadResume: uploadResumeMutation,
    isComplete,
    isError,
    isTimeout,
    status: data?.status ?? 'pending',
    data,
    isResumeBasicResultsComplete: !!resumeBasicResults || isComplete,
    isResumeWorkExperienceResultsComplete:
      !!resumeWorkExperienceResults || isComplete,
    isResumeSpecialtyResultsComplete: !!resumeSpecialtyResults || isComplete,
    isAccountInfoComplete: !!accountInfo || isComplete,
    resumeBasicResults,
    resumeWorkExperienceResults,
    resumeSpecialtyResults,
    accountInfo,
    taskId,
  };
};
